import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import Spinner from "./Spinner.jsx";
import CoinAmount from "./CoinAmount.jsx";

/**
 * Generic inspect window / modal.
 *
 * Intended for re-use (items, user profiles, etc.).
 */
function InspectWindow({
  visible,
  title,
  itemIcon,
  itemCount,
  itemType,
  imageSrc,
  onClose,
  accentColor,
  fallbackText,
  isImageLoading,
  buyPrice,
  sellPrice,
  onCopy,
  onPrev,
  onNext,
  children,
}) {
  const borderColor = accentColor || "#234";
  const normalizedCount = Math.max(0, Number(itemCount) || 0);
  const normalizedBuyPrice = Math.max(0, Number(buyPrice) || 0);
  const normalizedSellPrice = Math.max(0, Number(sellPrice) || 0);
  const isStackType = itemType === "CraftingMaterial" || itemType === "Container";

  const totalBuyPrice = normalizedBuyPrice * normalizedCount;
  const totalSellPrice = normalizedSellPrice * normalizedCount;
  const stackCount = 250;
  const stackBuyPrice = normalizedBuyPrice * stackCount;
  const stackSellPrice = normalizedSellPrice * stackCount;

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  // Keyboard navigation: left/right arrows for prev/next while visible
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!visible) return;
      if (e.key === "ArrowLeft" && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === "ArrowRight" && onNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (typeof onClose === "function") {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, onPrev, onNext, onClose]);

  if (!visible) return null;

  return createPortal(
    <div>
      {/* Dim background behind everything unless a caller places content above this z-index */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(2, 10, 30, 0.75)",
          backdropFilter: "blur(4px)",
          zIndex: 1001,
        }}
        onClick={handleClose}
      />

      {/* Modal container above overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1003,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            border: "1px solid rgba(80, 180, 255, 0.4)",
            borderRadius: 20,
            boxShadow:
              "0 0 40px rgba(0, 150, 255, 0.25), 0 8px 24px rgba(0, 0, 0, 0.7)",
            padding: "24px 28px 24px 28px",
            maxWidth: "96vw",
            maxHeight: "98vh",
            width: "auto",
            boxSizing: "border-box",
            textAlign: "center",
            background:
              "linear-gradient(160deg, rgba(10, 35, 70, 0.97) 0%, rgba(5, 20, 50, 0.98) 100%)",
            backdropFilter: "blur(16px)",
            pointerEvents: "auto",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 16,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Previous arrow */}
          {onPrev && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrev();
              }}
              style={{
                position: "absolute",
                left: -40,
                top: "50%",
                transform: "translateY(-50%)",
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid rgba(144, 224, 239, 0.6)",
                background:
                  "radial-gradient(circle at 30% 0%, #012a4a, #001219)",
                color: "#e0f7fa",
                fontWeight: "bold",
                fontSize: 18,
                cursor: "pointer",
                boxShadow: "0 3px 9px rgba(0, 0, 0, 0.7)",
              }}
              aria-label="Previous item"
            >
              
              ‹
            </button>
          )}

          {/* Next arrow */}
          {onNext && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              style={{
                position: "absolute",
                right: -40,
                top: "50%",
                transform: "translateY(-50%)",
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid rgba(144, 224, 239, 0.6)",
                background:
                  "radial-gradient(circle at 30% 0%, #012a4a, #001219)",
                color: "#e0f7fa",
                fontWeight: "bold",
                fontSize: 18,
                cursor: "pointer",
                boxShadow: "0 3px 9px rgba(0, 0, 0, 0.7)",
              }}
              aria-label="Next item"
            >
              
              ›
            </button>
          )}

          {/* Close button */}
          <button
            onClick={handleClose}
            style={{
              position: "absolute",
              top: 10,
              right: 12,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "1px solid rgba(144, 224, 239, 0.6)",
              background:
                "radial-gradient(circle at 30% 0%, #012a4a, #001219)",
              color: "#e0f7fa",
              fontWeight: "bold",
              fontSize: 16,
              cursor: "pointer",
              boxShadow: "0 3px 9px rgba(0, 0, 0, 0.7)",
            }}
            aria-label="Close inspect window"
          >
            ×
          </button>

          {/* Title */}
          {title && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                fontWeight: "800",
                fontSize: 20,
                marginBottom: 4,
                color: "#40e8ff",
                textShadow: "0 0 14px rgba(60, 200, 255, 0.8)",
              }}
            >
              {itemIcon ? (
                <img
                  src={itemIcon}
                  alt=""
                  style={{ width: 26, height: 26, objectFit: "contain", flexShrink: 0 }}
                />
              ) : null}
              {title}
            </div>
          )}

          {/* Image preview, if provided */}
          {imageSrc && (
            <img
              src={imageSrc}
              alt={typeof title === "string" ? title : "Inspect preview"}
              style={{
                maxWidth: "92vw",
                // Leave room for title, close button and copy button
                maxHeight: "calc(90vh - 150px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 14,
                border: `2px solid ${borderColor}`,
                background: "#182040",
                boxShadow:
                  "0 0 24px rgba(0, 180, 255, 0.35), 0 6px 18px rgba(0, 0, 0, 0.85)",
              }}
            />
          )}

          {!imageSrc && isImageLoading && (
            <div
              style={{
                padding: "18px 12px",
                color: "#cbe8ff",
                fontSize: 14,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Spinner />
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  opacity: 0.9,
                }}
              >
                Loading image
              </div>
            </div>
          )}

          {!imageSrc && !isImageLoading && fallbackText && (
            <div
              style={{
                padding: "18px 12px",
                color: "#cbe8ff",
                fontSize: 14,
              }}
            >
              {fallbackText}
            </div>
          )}

          <div
            style={{
              width: "100%",
              borderTop: "1px solid rgba(144, 224, 239, 0.22)",
              paddingTop: 10,
              display: "grid",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#9be7ff", fontSize: 14 }}>
              <span>Buy price</span>
              <CoinAmount value={buyPrice} color="#e0f7fa" />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#9be7ff", fontSize: 14 }}>
              <span>Sell price</span>
              <CoinAmount value={sellPrice} color="#e0f7fa" />
            </div>

            {normalizedCount > 0 && normalizedCount !== stackCount && normalizedCount !== 1 &&(
              <>
                <div style={{ marginTop: 4, borderTop: "1px solid rgba(144, 224, 239, 0.16)", paddingTop: 8, fontSize: 12, color: "rgba(203, 232, 255, 0.88)", textAlign: "left" }}>
                  Total for current count ({normalizedCount})
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#9be7ff", fontSize: 14 }}>
                  <span>Buy total</span>
                  <CoinAmount value={totalBuyPrice} color="#e0f7fa" />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#9be7ff", fontSize: 14 }}>
                  <span>Sell total</span>
                  <CoinAmount value={totalSellPrice} color="#e0f7fa" />
                </div>
              </>
            )}

            {isStackType && (
              <>
                <div style={{ marginTop: 4, borderTop: "1px solid rgba(144, 224, 239, 0.16)", paddingTop: 8, fontSize: 12, color: "rgba(203, 232, 255, 0.88)", textAlign: "left" }}>
                  Stack value ({stackCount})
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#9be7ff", fontSize: 14 }}>
                  <span>Buy stack</span>
                  <CoinAmount value={stackBuyPrice} color="#e0f7fa" />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#9be7ff", fontSize: 14 }}>
                  <span>Sell stack</span>
                  <CoinAmount value={stackSellPrice} color="#e0f7fa" />
                </div>
              </>
            )}
          </div>

          {onCopy && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCopy();
              }}
              style={{
                marginTop: 8,
                padding: "8px 18px",
                borderRadius: 999,
                border: "1px solid rgba(0, 180, 216, 0.7)",
                background:
                  "radial-gradient(circle at 30% 0%, #023e8a, #03045e)",
                color: "#e0f7fa",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                boxShadow: "0 3px 9px rgba(0, 0, 0, 0.7)",
              }}
            >
              Copy chat link
            </button>
          )}

          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default InspectWindow;
